import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { gameAPI } from '../../services/gameAPI';

const normalizeName = (value) => (value || '').trim();

const DisplayNameEditor = ({ initialFallbackName = '', compact = false, onSaved }) => {
  const fallbackName = normalizeName(initialFallbackName);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedName, setSavedName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;

    const loadProfile = async () => {
      setLoaded(false);
      setError('');

      try {
        const profile = await gameAPI.getUserProfile();
        if (ignore) {
          return;
        }

        const appDisplayName = normalizeName(profile?.display_name);
        const initialName = appDisplayName || fallbackName;
        setSavedName(appDisplayName);
        setDisplayName(initialName);
        setMessage(appDisplayName ? `Saved as ${appDisplayName}` : '');
      } catch (loadError) {
        if (ignore) {
          return;
        }
        console.error('Failed to load user profile:', loadError);
        setDisplayName(fallbackName);
        setError('Could not load profile.');
      } finally {
        if (!ignore) {
          setLoaded(true);
        }
      }
    };

    loadProfile();

    return () => {
      ignore = true;
    };
  }, [fallbackName]);

  const trimmedName = normalizeName(displayName);
  const validationMessage = useMemo(() => {
    if (!trimmedName) {
      return 'Enter a display name.';
    }
    if (trimmedName.length > 100) {
      return 'Display name must be 100 characters or less.';
    }
    return '';
  }, [trimmedName]);

  const hasChanges = trimmedName !== savedName;
  const saveDisabled = saving || !loaded || Boolean(validationMessage) || !hasChanges;

  const handleSave = async () => {
    if (saveDisabled) {
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const updatedProfile = await gameAPI.updateUserProfile(trimmedName);
      const updatedName = normalizeName(updatedProfile?.display_name || trimmedName);
      setSavedName(updatedName);
      setDisplayName(updatedName);
      setMessage('Display name saved.');
      onSaved?.(updatedName);
    } catch (saveError) {
      console.error('Failed to save display name:', saveError);
      setError('Could not save display name.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={
        compact
          ? 'w-64 rounded-md border border-amber-800/20 bg-white/95 p-3 shadow-lg'
          : 'rounded-lg border border-amber-800/20 bg-white/95 p-4 shadow-lg'
      }
    >
      <label className="block text-xs font-semibold uppercase tracking-wide text-amber-950/70" htmlFor="display-name-input">
        Display name
      </label>
      <div className="mt-2 flex items-center gap-2">
        <input
          id="display-name-input"
          type="text"
          value={displayName}
          maxLength={120}
          disabled={!loaded || saving}
          onChange={(event) => {
            setDisplayName(event.target.value);
            setMessage('');
            setError('');
          }}
          className="min-w-0 flex-1 rounded-md border border-amber-900/25 bg-white px-3 py-2 text-sm text-stone-900 shadow-inner outline-none transition focus:border-amber-700 focus:ring-2 focus:ring-amber-600/20 disabled:cursor-not-allowed disabled:opacity-60"
          aria-describedby="display-name-status"
        />
        <Button
          type="button"
          size="sm"
          disabled={saveDisabled}
          onClick={handleSave}
          className="bg-amber-800 text-white hover:bg-amber-900"
        >
          {saving ? 'Saving' : 'Save'}
        </Button>
      </div>
      <p
        id="display-name-status"
        className={`mt-2 min-h-4 text-xs ${error || validationMessage ? 'text-red-700' : 'text-stone-600'}`}
        aria-live="polite"
      >
        {error || validationMessage || message}
      </p>
    </div>
  );
};

export default DisplayNameEditor;
